import { yupResolver } from '@hookform/resolvers/yup'
import { EMerchantType } from '@if/core/types'
import { ControlledSelect, ControlledStringDateOnlyPicker } from '@if/mui'
import { Box, Button, MenuItem } from '@mui/material'
import {
  createMerchantLoanActivityMetricsFilterSchema,
  MerchantLoanActivityMetricsFilterFormValues,
} from '@src/containers/App/Statistics/LoanActivityMetrics/MerchantLoanActivityMetricsFilterSchema'
import { EARLIER_LUCAS_DATE } from '@src/types/Constants'
import { Merchant } from '@src/types/Merchant'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface MerchantLoanActivityMetricsFilterFormProps {
  currentMerchant: Merchant | null
  subsidiaryMerchants: Merchant[]
  onSubmit: (merchantId: string, fromDate: string, toDate: string) => void
}

export const MerchantLoanActivityMetricsFilterForm = ({
  currentMerchant,
  subsidiaryMerchants,
  onSubmit,
}: MerchantLoanActivityMetricsFilterFormProps) => {
  const { t } = useTranslation()

  const { control, handleSubmit } = useForm<MerchantLoanActivityMetricsFilterFormValues>({
    mode: 'onBlur',
    defaultValues: {
      merchantId: currentMerchant?.id || '',
      fromDate: '',
      toDate: '',
    } as MerchantLoanActivityMetricsFilterFormValues,
    resolver: yupResolver(createMerchantLoanActivityMetricsFilterSchema()),
  })

  const availableMerchants = useMemo(() => {
    if (!currentMerchant) return []

    const merchants = [currentMerchant]
    if (currentMerchant.merchantTypeId === EMerchantType.Banner) {
      merchants.push(...subsidiaryMerchants)
    }
    return merchants
  }, [currentMerchant, subsidiaryMerchants])

  const onFormSubmit = (data: MerchantLoanActivityMetricsFilterFormValues) => {
    onSubmit(data.merchantId, data.fromDate, data.toDate)
  }

  const today = new Date()
  const minDate = new Date(currentMerchant?.firstOnboardedOn || EARLIER_LUCAS_DATE)

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onFormSubmit)}
        sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}
      >
        <ControlledSelect
          name="merchantId"
          control={control}
          label={t('merchantStatistics.merchantLoanActivity.selectMerchant')}
          disableEmptyOption
          disabled={availableMerchants.length <= 1}
        >
          {availableMerchants.map((merchant) => (
            <MenuItem key={merchant.id} value={merchant.id}>
              {merchant.name}
            </MenuItem>
          ))}
        </ControlledSelect>

        <ControlledStringDateOnlyPicker
          name="fromDate"
          control={control}
          label={t('merchantStatistics.merchantLoanActivity.fromDate')}
          minDate={minDate}
          maxDate={today}
        />

        <ControlledStringDateOnlyPicker
          name="toDate"
          control={control}
          label={t('merchantStatistics.merchantLoanActivity.toDate')}
          maxDate={today}
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '56px' }}>
          <Button variant="contained" type="submit">
            {t('common.search')}
          </Button>
        </Box>
      </Box>
    </>
  )
}
